import { onUnmounted } from 'vue'

/**
 * Composable for handling scroll end events with automatic fallback for unsupported browsers.
 *
 * The `scrollend` event is not supported by iOS < 26.2, including many of the versions we're required to support as per [Ticketure E-commerce Browser Support Policy](https://docs.google.com/document/d/1ohUh_rJ9cQDHONf9U4LAtNke0Gp5Dtg05cWYCUDkgE4).
 *
 * This composable provides a unified API that uses the native event when available and falls back
 * to a debounced timeout otherwise.
 *
 * @param callback - Function to call when scroll ends
 * @param fallbackDelay - Delay in ms for fallback timeout (default: 500ms)
 *
 * @example
 * ```ts
 * const { handleScrollEnd, scheduleScrollEndFallback, clearScrollEndFallback } = useScrollEnd(() => {
 *   console.log('Scroll ended!')
 * })
 *
 * // In template: @scrollend="handleScrollEnd"
 * // In scroll handler: scheduleScrollEndFallback()
 * ```
 */
export function useScrollEnd(callback: () => void, fallbackDelay = 500) {
  /** Feature detection for scrollend event (not supported on iOS < 26.2) */
  const supportsScrollEnd = typeof window !== 'undefined' && 'onscrollend' in window

  let fallbackTimeout: ReturnType<typeof setTimeout> | null = null

  /**
   * Clears any pending fallback timeout.
   *
   * @usage Call this when you trigger a programmatic scroll.
   */
  function clearScrollEndFallback() {
    if (fallbackTimeout) {
      clearTimeout(fallbackTimeout)
      fallbackTimeout = null
    }
  }

  /**
   * Core handler that clears fallback and invokes the callback.
   * Also called by the fallback timeout.
   * @usage Bind this to `@scrollend` on your scrollable element.
   */
  function handleScrollEnd() {
    clearScrollEndFallback()
    callback()
  }

  /**
   * Schedules fallback timeout for browsers that don't support scrollend.
   * Uses debounce pattern: clears and reschedules on each call.
   * @usage Call this in your scroll handler and after programmatic scrolls.
   */
  function scheduleScrollEndFallback() {
    if (supportsScrollEnd) return

    clearScrollEndFallback()
    fallbackTimeout = setTimeout(handleScrollEnd, fallbackDelay)
  }

  // Clean up timeout on component unmount
  onUnmounted(clearScrollEndFallback)

  return {
    supportsScrollEnd,
    handleScrollEnd,
    scheduleScrollEndFallback,
    clearScrollEndFallback,
  }
}
